import React from 'react';
import { Divider, IconButton, Stack, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { CartSummary } from '@api/ShoppingCart/ShoppingCart';
import { currencyFormatter } from '@utils/Numbers';

// Resumen del pedido, en el ORDEN en que se arma la cuenta (rediseño QA
// 2026-08-26, 5ª ronda — pedido de Irving):
//
//   Subtotal → descuentos que bajan la cuenta (promoción, envío, PUNTOS)
//   → TOTAL → Propina → y hasta abajo los PAGOS ya registrados.
//
// Antes "Pago con Puntos" salía DESPUÉS del total (aunque el total ya venía sin
// puntos) y se leía como si fuera un pago más. Regla nueva: **todo renglón que
// se pueda quitar trae su bote de basura aquí** — el carrito se scrollea y el
// resumen es donde el cajero corrige la cuenta.
interface OrderTotalsProps {
    getSubtotal: () => number;
    getPromotion: () => number;
    getTotal: () => number;
    getTotalCash: () => number;
    shoppingCart?: CartSummary;
    rewardAmount: number;
    // Propina (D2): NO es venta — no entra a Total; el efectivo/tarjeta sí la traen
    tipAmount?: number;
    onRemoveTip?: () => void;
    creditCardAmount: number;
    bankTransferAmount: number;
    // Efectivo registrado en parcialidades (pago libre): baja el restante a
    // cobrar; el "Pago en efectivo" de abajo sigue siendo el TOTAL en efectivo
    cashRegisteredAmount?: number;
    onRemoveCreditCard: () => void;
    onRemoveBankTransfer: () => void;
    onRemoveCashRegistered?: () => void;
    // Quitar lo que baja/sube la cuenta desde el propio resumen (el carrito ya
    // los ofrece, pero se scrollea con las líneas)
    onRemovePoints?: () => void;
    onRemovePromo?: () => void;
    onRemoveShipping?: () => void;
}

interface SummaryRowProps {
    label: string;
    amount: string;
    color?: string;
    onRemove?: () => void;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, amount, color, onRemove }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" minHeight={28}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
            {onRemove && (
                <IconButton
                    size="small"
                    aria-label={`Quitar ${label}`}
                    onClick={onRemove}
                    sx={{ p: 0.25, ml: -0.25 }}
                >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            )}
            <Typography variant="body1" fontWeight={600}>
                {label}
            </Typography>
        </Stack>
        <Typography variant="body1" fontWeight={600} color={color}>
            {amount}
        </Typography>
    </Stack>
);

export const OrderTotals: React.FC<OrderTotalsProps> = ({
    getSubtotal,
    getPromotion,
    getTotal,
    getTotalCash,
    shoppingCart,
    rewardAmount,
    tipAmount,
    onRemoveTip,
    creditCardAmount,
    bankTransferAmount,
    cashRegisteredAmount,
    onRemoveCreditCard,
    onRemoveBankTransfer,
    onRemoveCashRegistered,
    onRemovePoints,
    onRemovePromo,
    onRemoveShipping
}) => {
    const shippingCost = shoppingCart?.shippingCost || 0;
    const tip = tipAmount || 0;
    const cashRegistered = cashRegisteredAmount || 0;

    return (
        // Un solo Stack (ajuste 2026-08-26, calibrado 2026-08-27 = D4 del plan):
        // antes cada renglón era hijo directo del Stack del POS, con su
        // spacing={2} — 16 px entre cada uno, ~100 px de aire para dar 6 números.
        // El ahorro real vino de agrupar, NO de encoger las letras: la primera
        // versión bajó todo a body2/h6 y quedó ilegible, así que las tipografías
        // volvieron a body1/h5 y lo que se conserva es este spacing.
        <Stack spacing={0.5}>
            <Divider />

            {/* ----- La cuenta: subtotal y lo que la mueve ----- */}
            <SummaryRow label="Subtotal" amount={currencyFormatter(getSubtotal())} />
            {shoppingCart?.promotionalCode && (
                <SummaryRow
                    // El código va AQUÍ, entre paréntesis (ajuste de Irving): el
                    // chip de arriba solo indica que hay promoción
                    label={`Promoción (${shoppingCart.promotionalCode.code})`}
                    amount={`-${currencyFormatter(getPromotion())}`}
                    color="darkred"
                    onRemove={onRemovePromo}
                />
            )}
            {shippingCost > 0 && (
                <SummaryRow
                    label="Envio"
                    amount={currencyFormatter(shippingCost)}
                    color="green"
                    onRemove={onRemoveShipping}
                />
            )}
            {rewardAmount > 0 && (
                <SummaryRow
                    label="Pago con Puntos"
                    amount={`-${currencyFormatter(rewardAmount)}`}
                    onRemove={onRemovePoints}
                />
            )}

            {/* ----- El total (ya sin promoción ni puntos) ----- */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" py={0.25}>
                <Typography variant="h5" fontWeight={700}>
                    Total
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                    {currencyFormatter(getTotal())}
                </Typography>
            </Stack>

            {/* ----- Propina: se suma a lo que se cobra, NO es venta (D2) ----- */}
            {tip > 0 && (
                <SummaryRow
                    label="Propina"
                    amount={`+${currencyFormatter(tip)}`}
                    color="green"
                    onRemove={onRemoveTip}
                />
            )}

            {/* ----- Pagos ya registrados (bajan el restante a cobrar) ----- */}
            {creditCardAmount > 0 && (
                <SummaryRow
                    label="Pago con Tarjeta"
                    amount={`- ${creditCardAmount.toFixed(2)}`}
                    onRemove={onRemoveCreditCard}
                />
            )}
            {bankTransferAmount > 0 && (
                <SummaryRow
                    label="Pago con Transferencia"
                    amount={`- ${bankTransferAmount.toFixed(2)}`}
                    onRemove={onRemoveBankTransfer}
                />
            )}
            {cashRegistered > 0 && (
                <SummaryRow
                    label="Efectivo recibido"
                    amount={`- ${cashRegistered.toFixed(2)}`}
                    onRemove={onRemoveCashRegistered}
                />
            )}
            {creditCardAmount + bankTransferAmount !== getTotal() + tip && (
                <SummaryRow label="Pago en efectivo" amount={getTotalCash().toFixed(2)} />
            )}
            <Divider />
        </Stack>
    );
};
